import * as THREE from 'three';

import { getSharedStandardMaterial } from '../../../../../utils/threeResourcePool';

import { FAB_COLORS, FAB_BODY_SHADING, FAB_LED_INTENSITY } from '../../rendering/fabColors';

import {

  createFabClearWhiteDistantGlass,

  createFabClearWhitePhysicalGlass,

  createFabClearWhiteSidePhysicalGlass,

} from '../../rendering/fabGlassMaterials';



/** Reference-matched semiconductor FAB palette (re-exports central colors). */

export const FAB_TOOL_PALETTE = {

  /** Reference silver-white equipment shell. */
  bodyWhite: '#E6EAEE',

  bodyGray: '#C8D0D8',

  bodyBright: '#F2F5F8',

  graphite: FAB_COLORS.graphite,

  graphiteMid: FAB_COLORS.blueGraphite,

  graphiteDeep: FAB_COLORS.darkGraphite,

  frameInner: FAB_COLORS.frameInner,

  stainless: FAB_COLORS.stainless,

  brushedMetal: FAB_COLORS.brushedMetal,

  chamberGlass: FAB_COLORS.whiteGlass,

  smokedGlass: FAB_COLORS.clearGlass,

  sideGlass: FAB_COLORS.whiteGlassTint,

  ledCyan: FAB_COLORS.cyan,

  ledCyanSoft: FAB_COLORS.cyanSoft,

  ledGreen: FAB_COLORS.green,

  ledAmber: FAB_COLORS.amber,

  ledRed: FAB_COLORS.red,

  vent: FAB_COLORS.metal,

  seam: FAB_COLORS.brushedMetal,

  loadPortBody: FAB_COLORS.loadPortBody,

  loadPortRecess: FAB_COLORS.loadPortRecess,

  foupBody: FAB_COLORS.foupBody,

  foupLid: FAB_COLORS.foupTop,

} as const;



/** Center chamber window — cleanroom clear white glass (reference). */

export function getFabClearChamberGlassMaterial(opacity = 1, quality: 'high' | 'low' = 'high'): THREE.Material {

  if (quality === 'low') return getFabChamberGlassDistantMaterial(opacity);

  return createFabClearWhitePhysicalGlass(opacity, 'fab-process');

}



/** Side accent panels — lighter clear tint. */

export function getFabBlueGlassMaterial(opacity = 1, quality: 'high' | 'low' = 'high'): THREE.Material {

  if (quality === 'low') return getFabChamberGlassDistantMaterial(opacity);

  return createFabClearWhiteSidePhysicalGlass(opacity, 'fab-process');

}



/** Distant / instanced chamber glass. */

export function getFabChamberGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabClearWhiteDistantGlass(opacity);

}



export function getFabBodyMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.bodyWhite,

    ...FAB_BODY_SHADING.white,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabWhiteBrightMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.bodyBright,

    ...FAB_BODY_SHADING.bright,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabLightGrayMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.bodyGray,

    ...FAB_BODY_SHADING.lightGray,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabGraphiteMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.graphite,

    ...FAB_BODY_SHADING.graphite,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabDarkLowerMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.graphiteDeep,

    ...FAB_BODY_SHADING.darkGraphite,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabBlueGraphiteMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.graphiteMid,

    metalness: 0.45,

    roughness: 0.42,

    envMapIntensity: 0.68,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabSmokedGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getFabChamberGlassDistantMaterial(opacity);

}



export function getFabSmokedGlassMaterial(opacity = 1, quality: 'high' | 'low' = 'high'): THREE.Material {

  if (quality === 'low') return getFabChamberGlassDistantMaterial(opacity);

  return createFabClearWhitePhysicalGlass(opacity, 'fab-process-smoked');

}



export function getFabGlassMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getFabSmokedGlassDistantMaterial(opacity);

}



export function getFabLedMaterial(color: string, intensity: number, opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color,

    emissive: color,

    emissiveIntensity: intensity,

    metalness: 0.05,

    roughness: 0.38,

    toneMapped: false,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFabCyanEdgeMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getFabLedMaterial(FAB_TOOL_PALETTE.ledCyan, FAB_LED_INTENSITY.cyanEdge, opacity);

}



export function getFabStainlessMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.stainless,

    ...FAB_BODY_SHADING.silver,

    opacity,

    transparent: opacity < 1,

  });

}



export function getLoadPortFrameMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_COLORS.bodySilver,

    metalness: 0.2,

    roughness: 0.36,

    envMapIntensity: 0.78,

    opacity,

    transparent: opacity < 1,

  });

}



export function getLoadPortBodyMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.loadPortBody,

    ...FAB_BODY_SHADING.white,

    opacity,

    transparent: opacity < 1,

  });

}



export function getLoadPortRecessMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.loadPortRecess,

    ...FAB_BODY_SHADING.graphite,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFoupBodyMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.foupBody,

    metalness: 0.24,

    roughness: 0.46,

    envMapIntensity: 0.58,

    opacity,

    transparent: opacity < 1,

  });

}



export function getFoupLidMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_TOOL_PALETTE.foupLid,

    metalness: 0.2,

    roughness: 0.42,

    envMapIntensity: 0.62,

    opacity,

    transparent: opacity < 1,

  });

}



export function statusTowerColors(status: string): { green: number; amber: number; red: number } {

  switch (status) {

    case 'RUNNING':

      return { green: 0.85, amber: 0.05, red: 0.05 };

    case 'IDLE':

      return { green: 0.12, amber: 0.75, red: 0.05 };

    case 'WARNING':

      return { green: 0.05, amber: 0.85, red: 0.05 };

    case 'ERROR':

      return { green: 0.05, amber: 0.1, red: 0.9 };

    default:

      return { green: 0.15, amber: 0.55, red: 0.05 };

  }

}


